import { defaultBoard } from "@kourai/api/home-board";
import { createContext, type FC, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccessGuard } from "@/hooks/use-access-guard";
import { useHomeBoard } from "@/hooks/use-home-board";
import { useHomeOnboarding } from "@/hooks/use-home-onboarding";
import { useWorkspace } from "@/hooks/use-workspace";
import { useSession } from "@/lib/auth-client";
import { isWideBoard } from "@/lib/home/layout";
import { offerStarters, showTip } from "@/lib/home/onboarding";
import { ComposerModelPicker } from "@/routes/dashboard/components/assistant/composer-model-picker";
import { SearchEngineSelector } from "@/routes/dashboard/components/assistant/search-engine-picker";
import { Thread } from "@/routes/dashboard/components/assistant/thread";
import {
  HomeBoard,
  type HomeBoardProps,
} from "@/routes/dashboard/components/home/board/home-board";
import { PinToHomeProvider } from "@/routes/dashboard/components/home/board/pin-to-home";
import { HomeHeading } from "@/routes/dashboard/components/home/home-heading";
import { RecentActivity } from "@/routes/dashboard/components/home/recent-activity";
import { WebBar } from "@/routes/dashboard/components/home/web-bar";
import { AccessBlocked } from "@/shared/access-guard";

const HomeWelcome: FC = () => <HomeHeading />;

/** The board under the command box; Thread renders this slot, so the board comes by context. */
const BoardContext = createContext<HomeBoardProps | null>(null);
const HomeFooter: FC = () => {
  const props = useContext(BoardContext);
  return props ? <HomeBoard {...props} /> : null;
};

/** The page widens from 760px to about 1120px once the board has more than Recent. */
const WIDE_BOARD = "70rem";

const noEdit = () => {};
const NOBODY = { id: "", name: "" };

/**
 * Signed out: web search and URLs work; asking the AI needs an account. Under the box, a look at
 * the board people get once signed in (the default one, with its example tasks); using it, or
 * Customize, leads to sign in.
 */
function SignedOutHome() {
  const [value, setValue] = useState("");
  const [board] = useState(defaultBoard);
  return (
    <div className="flex h-full overflow-y-auto">
      <div
        className="mx-auto flex w-full flex-col px-4 pt-[18vh] pb-10"
        style={{ maxWidth: WIDE_BOARD }}
      >
        <HomeHeading />
        <div className="border-foreground focus-within:border-ring border transition-colors">
          <WebBar value={value} onValueChange={setValue} />
          <div className="border-border flex items-center justify-between gap-3 border-t px-3.5 py-3 text-sm">
            <span className="text-muted-foreground">Sign in to ask the AI and keep chats.</span>
            <Link
              to="/auth/signin"
              className="bg-primary text-primary-foreground hover:bg-brand hover:text-brand-foreground shrink-0 px-3 py-1.5 font-medium transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
        <div className="text-muted-foreground flex items-center gap-2 pt-3 text-xs">
          <SearchEngineSelector />
        </div>
        <RecentActivity types={["web"]} query={value} />
        {!value && (
          <HomeBoard
            board={board}
            update={noEdit}
            editing={false}
            onEditingChange={noEdit}
            workspace={NOBODY}
            user={NOBODY}
            signedOut
          />
        )}
      </div>
    </div>
  );
}

export function Dashboard() {
  const { isAllowed, reason } = useAccessGuard();
  if (reason?.type === "unauthenticated") {
    return <SignedOutHome />;
  }

  if (!isAllowed) {
    return <AccessBlocked reason={reason as Exclude<typeof reason, null>} />;
  }

  return <SignedInHome />;
}

const HOME_COMPONENTS = { Welcome: HomeWelcome, NewChatFooter: HomeFooter };

/** Signed in: the command box, then your board in this workspace (ADMIN-258). */
function SignedInHome() {
  const { data: session } = useSession();
  const { activeWorkspace, activeOrganizationId, isAdminOrOwner } = useWorkspace();
  // A Private workspace has nobody else to start from a starter.
  const shared = !!activeWorkspace && !activeWorkspace.slug.startsWith("private-");
  const user = session?.user;
  const userId = user?.id ?? "";
  const {
    board,
    source,
    update,
    starterVersion,
    starterOffer,
    publishStarter,
    clearStarter,
    answerStarterOffer,
  } = useHomeBoard(userId, activeOrganizationId);
  const [editing, setEditing] = useState(false);
  const onboarding = useHomeOnboarding(userId);
  const hasOwnBoard = source === "member";

  const workspace = useMemo(
    () => ({ id: activeOrganizationId ?? "", name: activeWorkspace?.name ?? "" }),
    [activeOrganizationId, activeWorkspace?.name],
  );
  const person = useMemo(() => ({ id: userId, name: user?.name ?? "" }), [userId, user?.name]);
  const boardProps = useMemo<HomeBoardProps>(
    () => ({
      board,
      update,
      editing,
      onEditingChange: setEditing,
      workspace,
      user: person,
      tip: !!onboarding.state && showTip(onboarding.state, hasOwnBoard),
      offerStarters: !!onboarding.state && offerStarters(onboarding.state, hasOwnBoard),
      onTipDismiss: () => onboarding.change({ tipDismissed: true }),
      onStartersOffered: () => onboarding.change({ startersOffered: true }),
      starterOffer: !!starterOffer,
      onStarterOffer: answerStarterOffer,
      manageStarter:
        shared && isAdminOrOwner
          ? {
              hasStarter: starterVersion !== undefined,
              publish: publishStarter,
              clear: clearStarter,
            }
          : undefined,
    }),
    [
      board,
      update,
      editing,
      workspace,
      person,
      onboarding,
      hasOwnBoard,
      starterOffer,
      answerStarterOffer,
      shared,
      isAdminOrOwner,
      starterVersion,
      publishStarter,
      clearStarter,
    ],
  );

  return (
    <BoardContext.Provider value={boardProps}>
      <PinToHomeProvider workspaceId={activeOrganizationId} update={update}>
        <Thread
          newChatMaxWidth={editing || isWideBoard(board) ? WIDE_BOARD : undefined}
          components={HOME_COMPONENTS}
          composerExtras={
            <div className="flex min-w-0 items-center gap-0.5">
              <SearchEngineSelector />
              <ComposerModelPicker />
            </div>
          }
        />
      </PinToHomeProvider>
    </BoardContext.Provider>
  );
}
